import { useState, useEffect } from 'react';
import { getDisplayUrl } from '../services/memoryService';
import logger from '../utils/logger';

/**
 * Custom hook to load and cache display URLs for cart/order items
 * @param {Array} items - Array of cart items or order items with memories
 * @param {string} urlField - Field name for the URL (default: 'figurine_url')
 * @returns {Object} - Map of item IDs to display URLs
 */
export const useDisplayUrls = (items = [], urlField = 'figurine_url') => {
  const [displayUrls, setDisplayUrls] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadDisplayUrls = async () => {
      if (!items || items.length === 0) {
        setDisplayUrls({});
        return;
      }

      setLoading(true);

      try {
        const urlPromises = items.map(async (item) => {
          const memory = item.memories || item;
          if (!memory) return null;

          const imageUrl = memory[urlField] || memory.photo_url;
          if (!imageUrl) return null;

          try {
            const displayUrl = await getDisplayUrl(imageUrl);
            return { id: item.id, displayUrl };
          } catch (error) {
            logger.error('Error getting display URL for item:', item.id, error);
            return { id: item.id, displayUrl: imageUrl };
          }
        });

        const results = await Promise.all(urlPromises);
        const urlMap = {};
        results.forEach((result) => {
          if (result) {
            urlMap[result.id] = result.displayUrl;
          }
        });

        setDisplayUrls(urlMap);
      } catch (error) {
        logger.error('Error loading display URLs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDisplayUrls();
  }, [items, urlField]);

  return { displayUrls, loading };
};
